import React, { useMemo, useRef, useEffect } from 'react';
import RecapSection from '../RecapSection';
import UmlautHelper from './UmlautHelper';
import ReportSoalButton from '../../../LanguageLearning/ReportSoalButton';
import { ReviewQuestionHeader } from '../shared/ReviewQuestionHeader';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const ShortAnswerReview = ({ question, userAnswer, correctAnswer, isCorrect, feedback, explanation, recap = '', annotations = [], langCode = null, reportProps = null }) => {
  const questionText = question.text || question['text-soal'] || question.question || '';
  const audioRef = useRef(null);
  const activeAnnotationRef = useRef(null);
  
  // Normalize answers
  const userAnswerText = typeof userAnswer === 'string' ? userAnswer : (userAnswer || '');
  const correctAnswerText = typeof correctAnswer === 'string' ? correctAnswer : (correctAnswer || '');

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        if (annId && streamUrl && annType === 'audio') {
          map[annId] = {
            annId,
            streamUrl,
            annType,
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    if (questionText && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(questionText, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          if (annId && annPath && annType === 'audio' && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            map[annId] = {
              annId,
              streamUrl,
              annType,
            };
          }
        });
      } catch (error) {
        console.warn('ShortAnswerReview: failed to parse annotations from HTML', error);
      }
    }

    return map;
  }, [question.annotations, questionText]);

  // Enhance HTML for question text with annotation support
  const enhancedQuestionText = useMemo(() => {
    if (!questionText || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return questionText;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(questionText, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          if (!node.classList.contains('practice-annotation')) {
            node.classList.add('practice-annotation');
          }
          if (!node.classList.contains('annotation-hidden')) {
            node.classList.add('annotation-hidden');
          }
          if (!node.classList.contains('annotation')) {
            node.classList.add('annotation');
          }
          
          if (!node.hasAttribute('tabindex')) {
            node.setAttribute('tabindex', '0');
          }
          
          if (!node.hasAttribute('role')) {
            node.setAttribute('role', 'button');
          }
          
          if (!node.hasAttribute('aria-label')) {
            node.setAttribute('aria-label', `Play audio`);
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.warn('ShortAnswerReview: failed to enhance question text HTML', error);
      return questionText;
    }
  }, [questionText, annotationsMap]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const playAnnotationAudio = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    
    if (!streamUrl) {
      console.warn('ShortAnswerReview: No streamUrl found in annotation:', annotation);
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
      audioRef.current.addEventListener('error', (e) => {
        console.error('ShortAnswerReview: Audio playback error:', e);
      });
    }

    const audioEl = audioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl
      .play()
      .catch((error) => {
        console.error('ShortAnswerReview: failed to play annotation audio', error);
      });
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    playAnnotationAudio(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;

  return (
    <div className="bg-[var(--card-bg)] rounded-xl p-6 mb-6 max-md:py-2 max-md:px-0">
      <ReviewQuestionHeader
        questionType="Short Answer"
        reportSlot={reportProps && (
          <ReportSoalButton
            type={reportProps.reportType}
            prosetId={reportProps.prosetId}
            soalId={reportProps.soalId}
            allowReReport={true}
            persistReportedState={true}
            reportText="Report this question"
          />
        )}
        currentQuestionNumber={reportProps?.currentQuestionNumber}
        totalQuestions={reportProps?.totalQuestions}
        score={reportProps?.score}
      />

      <div className="flex flex-col gap-5">
        {questionText && (
          <div className="text-base text-[var(--text-primary)] leading-[1.6] [&_*]:text-[var(--text-primary)]">
            <div
              onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
              onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
              dangerouslySetInnerHTML={{ __html: enhancedQuestionText }}
            />
          </div>
        )}

        <div className="p-4 md:p-4 max-md:p-4 max-md:mx-0 max-md:w-full rounded-lg mt-4">
          <h4 className="text-sm font-bold mb-3 text-[var(--text-primary)] uppercase tracking-wide flex items-center gap-2">
            Your Answer:
            <UmlautHelper showTooltip={false} langCode={langCode} />
          </h4>
          <div className="text-[15px] leading-[1.6] text-[var(--text-primary)]">
            {userAnswerText ? (
              <p>{userAnswerText}</p>
            ) : (
              <p className="text-[var(--text-tertiary)] italic text-sm">No answer provided</p>
            )}
          </div>
        </div>

        {correctAnswerText && (
          <div className="p-4 md:p-4 max-md:p-4 max-md:mx-0 max-md:w-full rounded-lg">
            <h4 className="text-sm font-bold mb-3 text-[var(--text-primary)] uppercase tracking-wide flex items-center gap-2">Correct Answer:</h4>
            <div className="text-[15px] leading-[1.6] text-[var(--text-primary)]">
              <p>{correctAnswerText}</p>
            </div>
          </div>
        )}

        {feedback && (
          <div className="mt-5 p-5 bg-[#fffbeb] dark:bg-[rgba(251,191,36,0.1)] rounded-lg border-l-4 border-amber-500 max-md:p-4 max-md:mx-0 max-md:w-full">
            <h4 className="text-base font-bold mb-3 text-orange-600 dark:text-orange-400 flex items-center">💡 Feedback:</h4>
            <div className="text-[15px] leading-[1.7] text-gray-800 dark:text-gray-200">
              <p className="m-0">{feedback}</p>
            </div>
          </div>
        )}

        {/* Explanation - di atas recap */}
        {explanation && (
          <div className={feedback ? 'mt-1' : 'mt-0'} style={{ marginBottom: 0 }}>
            <div className="bg-gradient-to-br from-[#fff9e6] to-[#ffeaa7] dark:from-[#3d3520] dark:to-[#4a3d1a] p-5 rounded-xl border-l-4 border-[#ffd93d] max-md:p-4 max-md:mx-0 max-md:w-full">
              <h3 className="text-base font-bold text-[#d68910] dark:text-[#fbbf24] mb-3 flex items-center gap-2">Penjelasan</h3>
              <p className="text-[15px] text-[var(--text-primary)] leading-relaxed m-0">{explanation}</p>
            </div>
          </div>
        )}

        <RecapSection recap={recap} annotations={annotations} question={question} />
      </div>
    </div>
  );
};

export default ShortAnswerReview;

